/**
 * Audit Service
 *
 * Generic audit log fetcher used across all pages.
 */

import { apiClient } from '../axios';
import { API_ENDPOINTS } from '../endpoints';
import type { ApiResponse } from '../types';
import type { AuditLogEntry, AuditLogResponse, AuditFieldChange } from '@/components/common';

// ============================================
// API Response Types (matches actual API shape)
// ============================================

interface AuditFieldLogItem {
  fieldName: string;
  oldValue: string | number | boolean | null;
  newValue: string | number | boolean | null;
}

interface AuditLogApiItem {
  id: string;
  auditId: string;
  userId: string;
  auditLogType: string;
  customType: string;
  readableMessage: string;
  fieldLogItems: AuditFieldLogItem[] | null;
  logDate: string;
  webRequestInfoModal: {
    ipAddress: string;
    userAgent: string;
    deviceType: string;
    timeZone: string | null;
    location: string | null;
    localTime: string;
    fullHostName: string;
  } | null;
  auditContextType: string;
}

interface AuditUserDetail {
  id: string;
  firstName: string;
  lastName: string;
  username: string;
  [key: string]: unknown;
}

interface AuditLogApiResponse {
  totalRecords: number;
  userdetails: Record<string, AuditUserDetail>;
  list: AuditLogApiItem[];
}

// ============================================
// Date parsing helper
// ============================================

/** Parse "dd-MM-yyyy HH:mm:ss" to ISO string */
function parseLogDate(raw: string): string {
  if (!raw) return '';
  // "01-04-2026 19:32:31" → "2026-04-01T19:32:31"
  const [datePart, timePart] = raw.split(' ');
  if (!datePart) return raw;
  const [dd, mm, yyyy] = datePart.split('-');
  if (!dd || !mm || !yyyy) return raw;
  return `${yyyy}-${mm}-${dd}T${timePart || '00:00:00'}`;
}

// ============================================
// Action type mapping
// ============================================

function mapAction(auditLogType: string): string {
  const upper = (auditLogType || '').toUpperCase();
  if (upper === 'CREATE') return 'CREATED';
  if (upper === 'UPDATE') return 'UPDATED';
  if (upper === 'DELETE') return 'DELETED';
  return upper || 'UPDATED';
}

// ============================================
// Service
// ============================================

/**
 * Fetch audit logs by auditId with pagination.
 * Maps the API response to the AuditLogEntry shape expected by AuditTimeline.
 */
export async function getAuditLogs(
  auditId: string,
  page: number,
  size: number
): Promise<AuditLogResponse> {
  const response = await apiClient.post<ApiResponse<AuditLogApiResponse>>(
    API_ENDPOINTS.AUDIT.LOGS_LIST,
    {
      auditId,
      paging: { page: page + 1, size },
    }
  );

  const data = response.data.result;
  const userMap = data.userdetails ?? {};

  const list: AuditLogEntry[] = (data.list ?? []).map((item) => {
    const userDetail = userMap[item.userId];
    const userName = userDetail
      ? [userDetail.firstName, userDetail.lastName].filter(Boolean).join(' ')
      : 'Unknown';

    const changes: AuditFieldChange[] | undefined = item.fieldLogItems
      ? item.fieldLogItems.map((f) => ({
          field: f.fieldName,
          oldValue: f.oldValue,
          newValue: f.newValue,
        }))
      : undefined;

    return {
      id: item.id,
      userName,
      action: mapAction(item.auditLogType),
      description: item.readableMessage || '',
      timestamp: parseLogDate(item.logDate),
      changes,
    };
  });

  return {
    list,
    totalRecords: data.totalRecords ?? 0,
  };
}

export const auditService = {
  getAuditLogs,
};
